import * as signalR from '@microsoft/signalr';
import { getAccessToken } from './api';

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';

interface HubRegistration {
  connection: signalR.HubConnection;
  status: ConnectionStatus;
  /** true dopo stopAllHubs (logout): interrompe il retry della start iniziale. */
  stopped?: boolean;
}

const hubs = new Map<string, HubRegistration>();
const listeners = new Set<() => void>();

export function onStatusChange(fn: () => void): () => void {
  listeners.add(fn);
  // Replay dello stato corrente al nuovo subscriber: una transizione avvenuta
  // TRA il primo render e la registrazione del listener (es. un figlio che monta
  // e chiama getHub PRIMA dell'effect del genitore — ordine effect React
  // figlio->genitore) verrebbe altrimenti persa, lasciando il pallino sullo
  // stato iniziale (disconnected) al primo boot finche' non arriva un evento
  // successivo. Notificando subito, il subscriber riallinea sempre.
  fn();
  return () => { listeners.delete(fn); };
}

function notify(): void { listeners.forEach(fn => fn()); }

export function getOverallStatus(): ConnectionStatus {
  if (hubs.size === 0) return 'disconnected';
  const statuses = [...hubs.values()].map(h => h.status);
  if (statuses.some(s => s === 'disconnected')) return 'disconnected';
  if (statuses.some(s => s === 'connecting')) return 'connecting';
  return 'connected';
}

// Retry pattern aggressivo per riconnessioni infinite (no abort dopo 4 try).
// SignalR di default tenta a 0s/2s/10s/30s e si arrende: in produzione
// significa che basta un breve hiccup di rete per restare scollegati
// (LED rosso permanente). Con questo array di delay seguito da una
// funzione che restituisce sempre 60s, il client riprova all'infinito.
const RECONNECT_DELAYS: signalR.IRetryPolicy = {
  nextRetryDelayInMilliseconds: (ctx) => {
    // Scaglione iniziale veloce poi 1 min costante.
    const fastSteps = [0, 2000, 5000, 10_000, 30_000];
    return ctx.previousRetryCount < fastSteps.length
      ? fastSteps[ctx.previousRetryCount]
      : 60_000;
  },
};

export function getHub(hubPath: string): signalR.HubConnection {
  const existing = hubs.get(hubPath);
  if (existing) return existing.connection;

  // I hub backend sono montati su /api/hubs/* (vedi Program.cs).
  // accessTokenFactory: SignalR rilegge il token ad OGNI negotiate/reconnect,
  // quindi se l'access viene rotato dal refresh flow di api.ts la riconnessione
  // successiva usa il token nuovo. Senza questa factory il token resta
  // "congelato" al primo connect e dopo 15 min (durata access) la connessione
  // cade definitivamente -> LED rosso.
  // Item #6: in cookie mode l'access vive in memoria (getAccessToken lo legge
  // da li'), in wrapper mode da localStorage. getAccessToken astrae la dualita'.
  const connection = new signalR.HubConnectionBuilder()
    .withUrl(hubPath, {
      accessTokenFactory: () => getAccessToken() ?? '',
    })
    .withAutomaticReconnect(RECONNECT_DELAYS)
    .build();

  const reg: HubRegistration = { connection, status: 'connecting' };
  hubs.set(hubPath, reg);

  connection.onreconnecting(() => { reg.status = 'connecting'; notify(); });
  connection.onreconnected(() => { reg.status = 'connected'; notify(); });
  connection.onclose(() => { reg.status = 'disconnected'; notify(); });

  // BUGFIX (Bryan, "pallino rosso fisso al primo accesso"): withAutomaticReconnect
  // copre SOLO le cadute DOPO una connessione riuscita. Se la start() INIZIALE
  // fallisce (tipico al cold start: access token scaduto in attesa del silent
  // refresh — l'interceptor axios cura le API ma il negotiate SignalR no),
  // prima restava 'disconnected' per sempre: rosso fisso finche' non riaprivi
  // l'app. Ora la start iniziale viene RITENTATA con backoff: ad ogni tentativo
  // accessTokenFactory rilegge il token (nel frattempo rinnovato dal refresh
  // flow) e la connessione si auto-guarisce. Il retry si interrompe al logout
  // (stopAllHubs -> reg.stopped).
  void startWithRetry(reg, hubPath);

  return connection;
}

/** Backoff della start iniziale: veloce all'inizio, poi 1 tentativo/minuto. */
const INITIAL_START_DELAYS_MS = [2_000, 5_000, 10_000, 30_000];

async function startWithRetry(reg: HubRegistration, hubPath: string): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    if (reg.stopped) return;
    reg.status = 'connecting';
    notify();
    try {
      await reg.connection.start();
      reg.status = 'connected';
      notify();
      return; // da qui in poi ci pensa withAutomaticReconnect
    } catch (err) {
      console.warn(`SignalR connect error on ${hubPath} (tentativo ${attempt + 1})`, err);
      reg.status = 'disconnected';
      notify();
      const delay = attempt < INITIAL_START_DELAYS_MS.length
        ? INITIAL_START_DELAYS_MS[attempt]
        : 60_000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

export function stopAllHubs(): void {
  for (const reg of hubs.values()) {
    reg.stopped = true; // interrompe l'eventuale retry della start iniziale
    reg.connection.stop().catch(() => { /* ignore */ });
  }
  hubs.clear();
  notify();
}
